// Ohana Property Services blog. Static post catalogue.
// Webhook-published posts live separately in MongoDB (`blog_posts` collection).
// Slugs, dates, and URLs are stable. Do not change them without coordinating
// with the babylovegrowth webhook configuration.

export const POSTS = [
  {
    slug: 'what-to-do-with-a-parents-belongings-after-they-pass',
    title: "What to Do With a Parent's Belongings After They Pass",
    date: '2026-06-12',
    category: 'Estate Cleanouts',
    author: 'Olga Ortega',
    excerpt:
      'When a parent passes, the belongings they leave behind become a logistical question and an emotional one. Here is how I walk families through it.',
    body: [
      { type: 'p', text: 'I spent over a decade as a social worker in Central Indiana before I founded Ohana Property & Transition Services. In that decade I sat with a lot of families in the first week after a parent had passed. The conversation almost always ended up in the same place. The house. The boxes. The closets. The garage. What do we do with all of it.' },
      { type: 'p', text: 'Here is what I tell families now. It is the same guidance I would give my own family.' },

      { type: 'h2', text: '1. Slow down before you start.' },
      { type: 'p', text: 'The first instinct after a parent passes is to get moving. Get the house cleared. Get the estate settled. Get past it.' },
      { type: 'p', text: 'That instinct is almost always wrong.' },
      { type: 'p', text: 'Unless probate court, a buyer, or a sibling flying in is forcing the calendar, give yourself two to four weeks before you make any big decision. The items that get thrown out in the first week of grief are the items families regret losing for years.' },

      { type: 'h2', text: '2. Decide what done looks like.' },
      { type: 'p', text: 'Before you sort anything, decide where the property is going next.' },
      {
        type: 'list',
        items: [
          'Listing with a realtor? You need broom-clean and depersonalized.',
          'Renting? You need utilities checked, locks rekeyed, and a deep clean.',
          'A family member moving in? You need sentimental items preserved.',
          'Selling as-is or tearing down? You only need to pull the items the family wants kept.',
        ],
      },
      { type: 'p', text: 'Pick the destination first. Every decision in the middle gets easier when you know where you are headed.' },

      { type: 'h2', text: '3. Protect the irreplaceable things first.' },
      { type: 'p', text: 'Before you sort a single drawer, walk every room and pull out the items that cannot be replaced.' },
      { type: 'p', text: 'Photographs. Letters. Military records. Family Bibles. Jewelry with meaning. Birth certificates. Social Security cards. Anything handwritten by the person who passed.' },
      { type: 'p', text: 'Put it all in one place. One closet. One drawer. One box. Tell every family member where it is. That step alone prevents most of the regrets I see.' },

      { type: 'h2', text: '4. Sort everything else into four piles.' },
      { type: 'p', text: 'Once the irreplaceable items are safe, every other item lands in one of four piles. This is the same framework we use on every estate cleanout at Ohana.' },
      {
        type: 'list',
        items: [
          'Keep. A family member is taking it.',
          'Sell. It has real resale value. List it or take it to an estate sale.',
          'Donate. It is usable. A nonprofit, partner, or family in need can use it.',
          'Discard. It is at the end of its useful life.',
        ],
      },
      { type: 'p', text: 'Color-coded sticky notes work. Painter\u2019s tape works. The goal is to make every decision on paper before anything goes in a truck.' },

      { type: 'h2', text: '5. Coordinate with the right people.' },
      { type: 'p', text: 'An estate cleanout almost always touches more than one professional.' },
      {
        type: 'list',
        items: [
          'The estate attorney or executor. For probate accounting.',
          'The realtor. For the listing timeline.',
          'An appraiser or estate sale specialist. For items with real value.',
          'A donation partner or cleanout company. For the bulk of the contents.',
          'Specialists. For items a standard cleanout cannot take. Hazardous waste. Medications. Certain electronics.',
        ],
      },
      { type: 'p', text: 'At Ohana we coordinate with every one of those people on every estate cleanout. Our written scope, photo documentation, and donation receipts go directly into the estate attorney\u2019s file.' },

      { type: 'h2', text: '6. Bring in help before you burn out.' },
      { type: 'p', text: 'Most families underestimate the cleanout. The physical labor is heavy. The emotional weight is heavier.' },
      { type: 'p', text: 'By day three, almost every family I work with says the same thing. I cannot do this alone anymore.' },
      { type: 'p', text: 'The right time to bring in a professional crew is before that point, not after. The trade is simple. A good crew handles the lifting, the loading, the truck, the disposal, the donation routing, and the broom-clean finish. Your job is to decide what to keep. That trade is almost always worth it.' },

      { type: 'h2', text: '7. Let the items do some good on the way out.' },
      { type: 'p', text: 'One of the reasons we built the Hearth Project at Ohana is that families kept telling me the same thing. They wanted Mom\u2019s furniture to go to another family. Not a landfill.' },
      { type: 'p', text: 'I agreed.' },
      { type: 'p', text: 'Every usable item from every Ohana estate cleanout goes to Indiana nonprofits, shelters, and families in need before anything is disposed of. For most of the families I work with, that detail is what makes the transition bearable.' },

      { type: 'h2', text: 'When you are ready.' },
      { type: 'p', text: 'If you are clearing a parent\u2019s home in Columbus, Indiana or anywhere in Central Indiana, send me photos and the address. We respond same day. We work at the pace your family needs us to work at.' },
      { type: 'p', text: 'There is no rush from my side. Just a careful crew that has done this hundreds of times.' },

      {
        type: 'download_cta',
        title: 'Want the full property transition guide?',
        body: 'Download our 13-page property transition guide with checklists for estate cleanouts, inheritance steps, and landlord turnovers.',
        label: 'Download the Guide (PDF)',
        href: '/ohana/property-transition-services-guide.pdf',
      },
    ],
  },

  {
    slug: 'how-to-help-a-parent-downsize-without-it-becoming-a-crisis',
    title: 'How to Help a Parent Downsize Without It Becoming a Crisis',
    date: '2026-06-10',
    category: 'Senior Downsizing',
    author: 'Olga Ortega',
    excerpt:
      'Helping an aging parent downsize is one of the highest-stakes conversations a family has. Here is how I walk families through it.',
    body: [
      { type: 'p', text: 'Almost every senior downsizing conversation I have witnessed has hit one of three walls.' },
      { type: 'p', text: 'The first wall is grief. The home holds decades of life. Leaving it feels like leaving the life.' },
      { type: 'p', text: 'The second wall is fear. Of dependence. Of decline. Of admitting that something has changed.' },
      { type: 'p', text: 'The third wall is logistics. The boxes. The furniture. The energy nobody has.' },
      { type: 'p', text: 'Your job as the adult child is not to overcome those walls. Your job is to navigate them with patience, with respect, and with a plan.' },

      { type: 'h2', text: '1. Start the conversation long before the move.' },
      { type: 'p', text: 'The single most common reason a downsizing turns into a crisis is that the conversation gets delayed until the move is required. A fall. A diagnosis. A spouse passing. By then every decision is being made under pressure.' },
      { type: 'p', text: 'If you can start the conversation a year before any move, every step that follows is easier. Even two years.' },
      { type: 'p', text: 'Pick a quiet weekend. Walk through the house together. Ask what your parent would want to take if they did move someday. Ask what they would want to do with the rest.' },
      { type: 'p', text: 'You are not making decisions in that conversation. You are opening the door.' },

      { type: 'h2', text: '2. Lead with their priorities.' },
      { type: 'p', text: 'It is tempting to lead with logistics. Square footage. The assisted living facility. The timeline. The budget.' },
      { type: 'p', text: 'Do not.' },
      { type: 'p', text: 'Lead with what your parent cares about. What rooms in the new place matter most. What furniture has to come. What hobbies have to stay possible. What family photos have to come with.' },
      { type: 'p', text: 'When the conversation starts with what they get to keep, the entire transition shifts.' },

      { type: 'h2', text: '3. Sort in three passes, not one.' },
      { type: 'p', text: 'Trying to make every keep/donate/sell/discard decision in one weekend is a recipe for exhaustion and conflict. Plan for three passes through the house, at least a week apart.' },
      {
        type: 'list',
        items: [
          'Pass 1. Identify items that are unambiguously coming with them. The easy yes pile.',
          'Pass 2. Identify items that are unambiguously going. Broken. Duplicate. Unused for years. The easy no pile.',
          'Pass 3. Tackle the middle. The items with memory but no practical role. This is where patience matters most.',
        ],
      },
      { type: 'p', text: 'When Ohana runs a senior downsizing in Columbus, Edinburgh, Franklin, Greenwood, or anywhere in Central Indiana, we structure the project around this three-pass pattern. The crew moves at the pace the family sets.' },

      { type: 'h2', text: '4. Honor the items that are not coming.' },
      { type: 'p', text: 'One of the hardest moments in a downsizing is the moment your parent realizes that not everything is coming with them.' },
      { type: 'p', text: 'A dining table. A bookshelf. A china set. Items with no practical role in the new place but with decades of memory attached.' },
      { type: 'p', text: 'What helps is knowing exactly where those items are going. To a daughter. To a grandchild. To a specific charity. To a family they have read about.' },
      { type: 'p', text: 'Through the Hearth Project, we route usable items from every Ohana downsizing job to Indiana donation partners. Many of our senior clients ask where their things ended up. We tell them. For a lot of our clients, that knowledge is what makes the transition feel complete.' },

      { type: 'h2', text: '5. Plan for two homes, briefly.' },
      { type: 'p', text: 'In most senior downsizing transitions there is a window where the parent has both a new home and the old one. Weeks, sometimes months.' },
      { type: 'p', text: 'Plan for it.' },
      { type: 'p', text: 'Set up the new home before the old one is empty. Move sentimental items first so the new place feels like theirs before the old place is gone. Give your parent a place to retreat to during the cleanout.' },
      { type: 'p', text: 'That window matters. It makes the final handoff feel less like an ending and more like a chapter close.' },

      { type: 'h2', text: '6. Use a partner who has done this before.' },
      { type: 'p', text: 'A senior downsizing is not a junk removal job. It is a transition. The wrong crew can make it harder.' },
      { type: 'p', text: 'Look for a partner who:' },
      {
        type: 'list',
        items: [
          'Has done senior downsizings before. Not just general junk removal.',
          'Is patient with multi-visit projects.',
          'Communicates clearly with the senior AND the adult children.',
          'Routes donations on purpose. Not just disposes of everything.',
          'Is bonded, insured, and can provide documentation.',
        ],
      },
      { type: 'p', text: 'Ohana was built for this work. I spent a decade in social work before founding the company. Most of that decade was spent with seniors and their families through exactly these transitions. The Ohana approach is the one I wished my families had access to back then.' },

      { type: 'h2', text: 'When you are ready to talk.' },
      { type: 'p', text: 'If a downsizing is on the horizon for your parent in Columbus, Bloomington, Indianapolis Southside, or anywhere in Central Indiana, send me a note. We can walk through the timeline. We can map the three passes onto your situation. We can quote the cleanout portion from photos.' },
      { type: 'p', text: 'We respond same day. We are happy to start the conversation months before any work begins.' },

      {
        type: 'download_cta',
        title: 'Want a structured downsizing checklist?',
        body: 'Our property transition guide includes a full senior downsizing section, from first conversation to final move.',
        label: 'Download the Guide (PDF)',
        href: '/ohana/property-transition-services-guide.pdf',
      },
    ],
  },

  {
    slug: 'i-inherited-a-property-in-indiana-now-what',
    title: 'I Inherited a Property in Indiana. Now What?',
    date: '2026-06-08',
    category: 'Inherited Properties',
    author: 'Olga Ortega',
    excerpt:
      'Most of the inherited-property calls I get come from out-of-state adult children. Here is the playbook I walk them through.',
    body: [
      { type: 'p', text: 'Most of the inherited-property calls Ohana receives come from out-of-state adult children. A parent passed in Columbus, Seymour, Franklin, Greenwood, or somewhere else in Central Indiana. The heir is in Chicago, Nashville, Atlanta, or further out, trying to figure out what to do with a house they cannot physically be at most weeks.' },
      { type: 'p', text: 'If that is your situation, this is the playbook I walk new clients through when they call cold from another state.' },

      { type: 'h2', text: 'Step 1. Confirm where you stand legally.' },
      { type: 'p', text: 'Before you spend a dollar on the property, confirm the legal status. In Indiana that usually means one of three scenarios.' },
      {
        type: 'list',
        items: [
          'The property passed via a Transfer-on-Death deed or joint tenancy. Title is yours.',
          'The property is going through probate. Title transfers when probate closes. You may have authority as executor or beneficiary in the meantime.',
          'The property is in a trust. The trustee has authority and you coordinate with them.',
        ],
      },
      { type: 'p', text: 'Before you sell, list, or do major cleanout work, get an Indiana estate attorney to confirm your authority in writing. This is not the place to guess.' },

      { type: 'h2', text: 'Step 2. Secure the property.' },
      { type: 'p', text: 'An unattended Indiana property is a liability. Pipes freeze in winter. Roofs leak after storms. Lawns overgrow and signal to the neighborhood that no one is checking on the home.' },
      { type: 'p', text: 'Within the first two weeks:' },
      {
        type: 'list',
        items: [
          'Change the locks. You do not always know who has a key.',
          'Confirm utilities are on. Heat in winter. Electric year-round.',
          'Forward or stop the mail.',
          'Notify the homeowner\u2019s insurance carrier. Most policies require notice within 30 to 60 days of the owner\u2019s death.',
          'Take photos of every room in case anything is later disputed.',
        ],
      },

      { type: 'h2', text: 'Step 3. Document the contents before anyone touches them.' },
      { type: 'p', text: 'Walk every room with a phone camera and take 5 to 10 photos per room before anyone, including family, moves anything. This protects the estate. It protects you. It gives you a record if a sibling asks later what happened to a specific item.' },
      { type: 'p', text: 'If you cannot do this yourself because you live out of state, Ohana can do it for you. A documented walkthrough is part of how we scope estate cleanouts remotely. We send a photo report back so you know exactly what is in the house before any sorting begins.' },

      { type: 'h2', text: 'Step 4. Decide the property\u2019s next chapter.' },
      { type: 'p', text: 'There are usually four paths an inherited Indiana property goes down.' },
      {
        type: 'list',
        items: [
          'Sell to a retail buyer through a listing agent. Needs a cleanout and pre-listing prep.',
          'Sell as-is to an investor or wholesaler. Only the items the family wants need to come out.',
          'Rent the property. Needs a turnover cleanout and likely some repair work.',
          'Hold for a family member to use later. Needs selective sorting with family priorities preserved.',
        ],
      },
      { type: 'p', text: 'Pick the path before you schedule the cleanout. Otherwise you will pay for work that does not match the destination.' },

      { type: 'h2', text: 'Step 5. Coordinate the cleanout remotely.' },
      { type: 'p', text: 'If you live out of state, the cleanout needs to happen without you being on-site. That means working with a partner who:' },
      {
        type: 'list',
        items: [
          'Will scope from photos and a property walkthrough video.',
          'Will set aside any items you flag and ship them back to you. We do this.',
          'Will route donations on purpose and document where items went.',
          'Will provide photo documentation of the final state. Every room. Broom-clean.',
          'Is bonded, insured, and provides receipts for estate accounting.',
        ],
      },
      { type: 'p', text: 'Ohana handles all of that on every out-of-state job. MBE and WBE certified. We coordinate directly with Indiana estate attorneys. We are comfortable being the boots-on-the-ground partner for an heir who is two states away.' },

      { type: 'h2', text: 'Step 6. Plan the handoff.' },
      { type: 'p', text: 'Once the cleanout is complete, the property has to land in whatever next state you picked in Step 4. That usually means:' },
      {
        type: 'list',
        items: [
          'Handing off to a realtor for photos and listing.',
          'Handing off to an investor for closing.',
          'Handing off to a property manager for tenant placement.',
          'Holding the keys until a family member is ready.',
        ],
      },
      { type: 'p', text: 'We leave every property broom-clean, with every room photographed and a utility status report ready for the next professional in the chain. For most out-of-state heirs, that handoff is the moment the transition actually feels over.' },

      { type: 'h2', text: 'The Hearth Project and inherited properties.' },
      { type: 'p', text: 'Inherited properties often contain decades of belongings. Furniture. Appliances. Clothing. Household goods. The family does not need any of it, but it still has life left in it.' },
      { type: 'p', text: 'Through the Hearth Project, we route those items first to Indiana nonprofits and families in need. For most of our out-of-state heirs, that detail matters. The home they could not visit is, in some small way, still doing some good.' },

      { type: 'h2', text: 'When you are ready.' },
      { type: 'p', text: 'Send the property address and any photos you have. We respond same day. We coordinate remotely with out-of-state heirs every week. We work directly with your Indiana estate attorney if you have one.' },

      {
        type: 'download_cta',
        title: 'Want the full inheritance playbook?',
        body: 'Our property transition guide walks through every step of an inherited-property transition, from securing the property to handing it off to a realtor.',
        label: 'Download the Guide (PDF)',
        href: '/ohana/property-transition-services-guide.pdf',
      },
    ],
  },

  {
    slug: 'why-realtors-in-columbus-need-a-reliable-pre-listing-cleanout-partner',
    title: 'Why Realtors in Columbus, IN Need a Reliable Pre-Listing Cleanout Partner',
    date: '2026-06-06',
    category: 'For Realtors',
    author: 'Olga Ortega',
    excerpt:
      'A listing that shows well sells faster and closer to ask. The difference often comes down to who you call for the pre-listing cleanout.',
    body: [
      { type: 'p', text: 'If you list properties in Columbus, Indiana or anywhere in Bartholomew County and Central Indiana, you already know the difference between a property that shows well and one that does not. It is rarely about the bones of the house. It is almost always about what is inside.' },
      { type: 'p', text: 'And what is inside is almost always a pre-listing cleanout problem.' },

      { type: 'h2', text: 'Why pre-listing cleanouts matter more than realtors say out loud.' },
      { type: 'p', text: 'A cluttered listing photographs as a cluttered listing. A house full of inherited contents photographs as a house full of inherited contents.' },
      { type: 'p', text: 'Buyers form an opinion in the first seven photos on Zillow. If those photos show a house that needs work the buyer cannot estimate, the buyer scrolls past.' },
      { type: 'p', text: 'The pattern is consistent. A clean, depersonalized property lists faster, gets more showings, and sells closer to asking than one that is not. The cleanout is part of the listing.' },

      { type: 'h2', text: 'What goes wrong with most pre-listing cleanouts.' },
      { type: 'p', text: 'Most Columbus realtors I work with have a version of the same story.' },
      { type: 'p', text: 'They had a vendor. The vendor did not show up. Or the vendor showed up and did not finish. Or the vendor finished but left the property in a state that still needed a second crew.' },
      { type: 'p', text: 'The listing date got pushed. The seller got frustrated. The realtor got blamed.' },
      { type: 'p', text: 'The cause is almost always the same. The vendor was a junk removal company, not a property transition company. A pre-listing cleanout is not just removing items. It is removing the right items, in the right order, leaving a property that the photographer can walk into and shoot, on the day the realtor needs it done.' },

      { type: 'h2', text: 'What a reliable pre-listing partner looks like.' },
      { type: 'p', text: 'If you are choosing a cleanout partner for your listings, these are the signals that matter.' },
      {
        type: 'list',
        items: [
          'Answers the phone. The single biggest predictor of whether the job gets done on time.',
          'Quotes from photos. Does not require an in-home estimate that costs you two days.',
          'Has a written scope before the job starts. Broom-clean. Depersonalized. Ready for photos.',
          'Coordinates directly with the seller or family when needed. You stay in the realtor role.',
          'Routes donations on purpose. Sellers feel better about the cleanout when items end up somewhere meaningful.',
          'Bonded, insured, certified. Bonded matters for occupied estate properties. Insured matters always. MBE and WBE certified matters for institutional referrals.',
          'Communicates clearly. Text on arrival. Text on completion. Photos of the finished property.',
        ],
      },

      { type: 'h2', text: 'Why Columbus realtors work with Ohana.' },
      { type: 'p', text: 'Ohana Property & Transition Services is a Columbus, Indiana company. Woman-owned. Latina-owned. MBE and WBE certified. Built around the pre-listing cleanout use case for Bartholomew County and Central Indiana realtors.' },
      {
        type: 'list',
        items: [
          'Photo-based quotes. Usually returned within 4 hours.',
          '24 to 48 hour scheduling on most Columbus cleanouts.',
          'Direct coordination with sellers, estate executors, or family. You stay strategic, not logistical.',
          'Broom-clean, photo-ready finish on every property.',
          'Written documentation, donation receipts, and disposal pathway records on request.',
          'A founder who spent a decade in social work and understands estate listings as well as anyone in the area.',
        ],
      },

      { type: 'h2', text: 'The donation angle that matters for sellers.' },
      { type: 'p', text: 'For estate and inherited-property listings (a big share of Columbus inventory), sellers care deeply about where their parent\u2019s belongings end up.' },
      { type: 'p', text: 'Through the Hearth Project, every Ohana cleanout routes usable items to Indiana donation partners before anything is disposed of.' },
      { type: 'p', text: 'That detail turns a one-time pre-listing cleanout into a relationship. And a referral.' },

      { type: 'h2', text: 'The vendor reliability that drives your listing timeline.' },
      { type: 'p', text: 'A pre-listing cleanout that runs on time means the photographer shoots on time. The listing goes live on time. The showings start on time. The offers come in on time.' },
      { type: 'p', text: 'A pre-listing cleanout that runs late means everything downstream runs late.' },
      { type: 'p', text: 'We treat your listing timeline like it is ours. Because for the seller, it effectively is.' },

      { type: 'h2', text: 'How to start a partnership.' },
      { type: 'p', text: 'If you list in Columbus, Indianapolis Southside, Franklin, Greenwood, Seymour, Edinburgh, Shelbyville, North Vernon, Greensburg, Bloomington, or anywhere in Central Indiana and you want a reliable pre-listing partner, the simplest first step is a conversation.' },
      { type: 'p', text: 'Email ohanas@ohanapropertyexperts.com or call (812) 302-6833. Tell us about your next listing. Even if it is two weeks out. We will scope the cleanout from photos.' },
      { type: 'p', text: 'Most realtors who work with us a second time become regular partners. The first job is the audition.' },

      {
        type: 'download_cta',
        title: 'Want the realtor partner sheet?',
        body: 'Our property transition guide includes the full pre-listing checklist Ohana uses with realtor partners.',
        label: 'Download the Guide (PDF)',
        href: '/ohana/property-transition-services-guide.pdf',
      },
    ],
  },

  {
    slug: '5-keys-to-a-calm-estate-cleanout',
    title: '5 Keys to a Calm Estate Cleanout',
    date: '2026-05-17',
    category: 'Estate Cleanouts',
    author: 'Olga Ortega',
    excerpt:
      'Estate cleanouts are emotionally taxing and operationally heavy. These five principles turn an overwhelming project into a structured transition.',
    body: [
      { type: 'p', text: 'Settling an estate is rarely just about the property. It is about a family, a chapter that is closing, and a long list of decisions that all seem to need answers at once.' },
      { type: 'p', text: 'Most people we work with say the same thing on the first call. I do not even know where to start.' },
      { type: 'p', text: 'This guide is meant to help with exactly that.' },
      { type: 'p', text: 'I founded Ohana Property & Transition Services after a decade as a social worker in Central Indiana. We have managed enough estate cleanouts across Columbus, Indianapolis Southside, Bloomington, Greenwood, Franklin, and Seymour to see the same patterns over and over. Families that follow these five principles move through the transition with less stress and fewer surprises.' },

      { type: 'h2', text: '1. Plan first. Lift second.' },
      { type: 'p', text: 'Before a single box gets moved, decide the order.' },
      { type: 'p', text: 'Walk every room. Identify the four piles every item is going to land in. Keep. Sell. Donate. Discard.' },
      { type: 'p', text: 'Do this on paper or on your phone. Do not try to make decisions item by item while the truck is in the driveway. That is how burnout happens. That is how heirlooms accidentally end up in a dumpster.' },
      { type: 'p', text: 'A simple rule. Plan the whole job in one sitting. Then execute room by room. Planning is cheap. Re-doing work is not.' },

      { type: 'h2', text: '2. Use a clear sorting system.' },
      { type: 'p', text: 'Once the plan exists, give every item a destination using the same four labels every time.' },
      {
        type: 'list',
        items: [
          'Keep. The family is taking it, storing it, or distributing it.',
          'Sell. It has real resale value. List it or route it to a buyer.',
          'Donate. It is usable. A charity, partner, or community channel can use it.',
          'Discard. It is at the end of its useful life.',
        ],
      },
      { type: 'p', text: 'Color-coded sticky notes or painter\u2019s tape work great. The fewer decisions left for the day of the cleanout, the faster and calmer the day. When Ohana handles a cleanout, we use this same framework. What you sorted is what gets executed.' },

      { type: 'h2', text: '3. Build in self-care.' },
      { type: 'p', text: 'Estate work is physically heavy. Emotionally it is heavier.' },
      { type: 'p', text: 'Most families underestimate both.' },
      { type: 'p', text: 'Schedule breaks. Eat lunch off-site. Stay hydrated. If a particular room belonged to the person you are grieving, give yourself permission to leave it for last. Or to ask someone else to handle it.' },
      { type: 'p', text: 'The cleanout itself takes a few days. The emotional weight takes longer. Pacing matters.' },

      { type: 'h2', text: '4. Know when to delegate.' },
      { type: 'p', text: 'There is a point in every estate cleanout where the math changes. The volume. The time available. The physical demand. The emotional cost. One of them crosses a line, and trying to do it all yourself stops making sense.' },
      { type: 'p', text: 'That is the moment to bring in a professional crew.' },
      { type: 'p', text: 'A good crew handles the lifting, the loading, the disposal, the donation routing, and the deep clean. Your job becomes deciding what to keep. That trade is almost always worth it.' },
      { type: 'p', text: 'We quote estate cleanouts from photos. We walk the family through scope before we schedule. No surprises on the day.' },

      { type: 'h2', text: '5. Plan the handoff.' },
      { type: 'p', text: 'The final principle most people miss. The cleanout is not the finish line. The handoff is.' },
      { type: 'p', text: 'Decide in advance what done looks like for your situation.' },
      {
        type: 'list',
        items: [
          'Ready to list. Photos can be taken. Broom-clean. No debris.',
          'Ready to rent. Utilities verified. Deep cleaned. Locks rekeyed.',
          'Ready for an heir to move in. Sentimental items preserved. Basic functionality restored.',
          'Ready for a contractor. Empty. Accessible. Utilities documented.',
        ],
      },
      { type: 'p', text: 'Knowing the handoff target up front lets you reverse-engineer everything else. It also tells your cleanout crew exactly what finished means to you. That is the single best way to make sure you do not get a half-done property back.' },

      { type: 'h2', text: 'How Ohana fits in.' },
      { type: 'p', text: 'Ohana handles estate cleanouts across Central Indiana with the same framework above. Our 5-step process (Assess, Sort, Coordinate, Execute, Transition) maps directly onto how families actually work through these projects. We bring the structure so you can focus on the family.' },
      { type: 'p', text: 'Through the Hearth Project, usable items from estate cleanouts go to families and nonprofits in Central Indiana rather than disposal. For most of the families we work with, that knowledge alone makes the transition easier.' },

      { type: 'h2', text: 'When you are ready.' },
      { type: 'p', text: 'Send us photos and the property address. ohanas@ohanapropertyexperts.com or the quote form on this site.' },
      { type: 'p', text: 'We respond same day. We scope from your photos. We can typically schedule within 24 to 48 hours across Columbus, Indianapolis Southside, Bloomington, Greenwood, Franklin, Seymour, Edinburgh, Shelbyville, North Vernon, Greensburg, and surrounding Central Indiana communities.' },

      {
        type: 'download_cta',
        title: 'Want the full guide?',
        body: 'Download our 13-page property transition guide.',
        label: 'Download the Guide (PDF)',
        href: '/ohana/property-transition-services-guide.pdf',
      },
    ],
  },

  {
    slug: 'what-items-cant-go-in-a-junk-removal-truck-in-indiana',
    title: "What Items Can't Go in a Junk Removal Truck in Indiana: A Central Indiana Guide",
    date: '2026-05-08',
    category: 'Cleanout Tips',
    author: 'Olga Ortega',
    excerpt:
      'Not everything can go in a junk removal truck in Indiana. Here is what Bartholomew County regulations say, and where to take the items we cannot haul.',
    body: [
      { type: 'p', text: 'The most common question we get before a cleanout is simple. Can you take everything?' },
      { type: 'p', text: 'Honest answer. Almost everything.' },
      { type: 'p', text: 'There are specific items Indiana law and Bartholomew County solid waste regulations prohibit from standard junk removal and landfill disposal. Knowing what those items are before we arrive saves time, prevents delays, and protects everyone involved.' },
      { type: 'p', text: 'This guide is based on Bartholomew County Solid Waste Management District (BCSWMD) guidelines. It applies to property cleanouts, estate cleanouts, and junk removal jobs in Columbus, Indianapolis Southside, Bloomington, Greenwood, Franklin, Seymour, Edinburgh, Shelbyville, North Vernon, Greensburg, and surrounding Central Indiana communities.' },

      { type: 'h2', text: 'Why some items are prohibited.' },
      { type: 'p', text: 'The Bartholomew County Landfill, like every licensed Indiana disposal facility, bans certain materials to protect groundwater, soil, and public health. Improper disposal of hazardous materials can result in fines for the property owner and the hauler.' },
      { type: 'p', text: 'We follow every BCSWMD and Indiana solid waste regulation on every job. That is non-negotiable.' },

      { type: 'h2', text: 'Items we cannot remove.' },
      { type: 'p', text: 'These are banned from standard junk removal and landfill disposal in Bartholomew County.' },
      {
        type: 'list',
        items: [
          'Hazardous household waste. Paints, solvents, pesticides, chemical products.',
          'Oil and oil-based products of any kind.',
          'Batteries of any type. Car, household, industrial.',
          'Electronics. TVs, computers, monitors, e-waste. They require certified recycling.',
          'Refrigerators, freezers, and CFC-containing appliances. They require special handling.',
          'Whole tires. Quartered tires are accepted at the landfill at additional cost.',
          'Solar panels.',
          'Medical waste, medications, and sharps.',
          'Any liquids or containers with liquids.',
          'Closed or sealed drums of any kind.',
          'Asbestos or anything suspected to contain asbestos.',
          'Biohazard materials of any kind.',
        ],
      },

      { type: 'h2', text: 'Where to take items we cannot take.' },
      { type: 'p', text: 'Just because we cannot haul it does not mean you are stuck with it. Bartholomew County has specific resources for each category.' },
      {
        type: 'list',
        items: [
          'Hazardous household waste. BCSWMD Tox-Away facility at 811 E. CR 450 S., Columbus IN. Monday through Friday, 8am to 4pm.',
          'Electronics. BCSWMD Electronics Recycling accepts TVs and computers. $20 per TV or monitor.',
          'Appliances with CFCs. Accepted at the Bartholomew County Landfill for $20 each with proper CFC handling.',
          'Batteries. BCSWMD recycling locations.',
          'Medications and sharps. BCSWMD accepts these for safe disposal.',
          'Yard waste. Separate programs through BCSWMD and the City of Columbus Department of Public Works.',
        ],
      },
      { type: 'p', text: 'For questions about specific items in Bartholomew County, contact BCSWMD at 812-376-2614 or bcswmd.com. For Columbus city residents, contact Columbus Department of Public Works at 812-376-2509 or columbus.in.gov/public-works.' },

      { type: 'h2', text: 'What we can take. Almost everything else.' },
      { type: 'p', text: 'Outside of the prohibited items above, Ohana handles the full range of residential and property cleanout materials.' },
      {
        type: 'list',
        items: [
          'Furniture. Sofas, chairs, tables, beds, dressers.',
          'General household junk and clutter.',
          'Appliances that do not contain CFCs.',
          'Clothing, linens, soft goods.',
          'Boxes, bags, miscellaneous household items.',
          'Construction debris and renovation waste.',
          'Yard waste and outdoor items.',
          'Exercise equipment and recreational items.',
          'Mattresses.',
          'Metal items and scrap.',
          'Estate contents and inherited property items.',
        ],
      },

      { type: 'h2', text: 'How the Hearth Project fits in.' },
      { type: 'p', text: 'When we remove items from a property, our first question is not where it goes to be disposed of. Our first question is whether it can be given a second life.' },
      { type: 'p', text: 'Through the Hearth Project, Ohana identifies usable furniture, household goods, and clothing during every cleanout. We redirect those items to families and nonprofits in Central Indiana before anything goes to the landfill.' },
      { type: 'p', text: 'Responsible removal means thinking about what happens after the truck leaves. That is how we work.' },

      { type: 'h2', text: 'Send us your photos before we arrive.' },
      { type: 'p', text: 'The best way to make sure your cleanout goes smoothly is to send us photos before we schedule. If there are items on the prohibited list, we flag them upfront and help you find the right disposal path. No surprises on the day.' },
      { type: 'p', text: 'Send photos and your property address to ohanas@ohanapropertyexperts.com or use the quote form on this site. We respond same day. We can typically schedule within 24 to 48 hours across Central Indiana.' },
    ],
  },

  {
    slug: 'estate-cleanout-vs-junk-removal-whats-the-difference',
    title: "Estate Cleanout vs. Junk Removal: What's the Difference and Which One Do You Need?",
    date: '2026-05-08',
    category: 'Cleanout Tips',
    author: 'Olga Ortega',
    excerpt:
      'Estate cleanouts and junk removal are not the same. Here is how to know which one your situation actually calls for.',
    body: [
      { type: 'p', text: 'When a property needs to be cleared, most people search for junk removal without realizing there is a real difference between junk removal and an estate cleanout.' },
      { type: 'p', text: 'The distinction matters. For pricing. For how the job gets handled. For how long it takes. For what happens to the items when they leave.' },
      { type: 'p', text: 'If you are dealing with an inherited property, a loved one\u2019s home, a foreclosure, or a full house that needs to be emptied before a sale or renovation, this guide will help you choose the right service.' },

      { type: 'h2', text: 'What is junk removal?' },
      { type: 'p', text: 'Junk removal is exactly what it sounds like. The removal of specific items or a defined load of unwanted material from a property.' },
      { type: 'p', text: 'It is faster. It is more straightforward. It is scoped around a clear set of items the client already knows they want gone.' },
      { type: 'p', text: 'Common junk removal jobs:' },
      {
        type: 'list',
        items: [
          'A few pieces of old furniture.',
          'Clearing a garage, basement, or storage unit.',
          'Hauling away appliances after a replacement.',
          'Removing debris after a renovation or yard project.',
          'Emptying a rental unit after a tenant moves out.',
        ],
      },
      { type: 'p', text: 'We scope these from photos. Price by volume. Complete most in one visit.' },

      { type: 'h2', text: 'What is an estate cleanout?' },
      { type: 'p', text: 'An estate cleanout is bigger.' },
      { type: 'p', text: 'It is the full contents of a home that need to be sorted, removed, donated, or disposed of. Estate cleanouts usually happen after a death in the family. Or when settling an inherited property. Or during a foreclosure. Or when transitioning a long-term residence.' },
      { type: 'p', text: 'Estate cleanouts are more complex than junk removal for several real reasons.' },
      {
        type: 'list',
        items: [
          'The full scope of items is often not known until the property is assessed.',
          'Some items may have sentimental or monetary value that family members want to review.',
          'Multiple trips may be needed for larger properties.',
          'Sorting for donation, recovery, and disposal takes time.',
          'The emotional context matters. Estate cleanouts often happen during hard family moments.',
        ],
      },
      { type: 'p', text: 'At Ohana we approach estate cleanouts with an extra layer of care. Clearing a loved one\u2019s home is not just a logistics job. It is a transition. We work at a pace that respects that. We still execute efficiently.' },

      { type: 'h2', text: 'Side by side. The key differences.' },
      { type: 'p', text: 'A straightforward comparison.' },
      {
        type: 'list',
        items: [
          'Scope. Junk removal handles defined items. Estate cleanouts cover full property contents.',
          'Timeline. Junk removal is typically one visit. Estate cleanouts may need multiple visits.',
          'Sorting. Junk removal items are pre-identified. Estate cleanouts often need on-site sorting.',
          'Complexity. Junk removal is straightforward. Estate cleanouts involve more assessment and coordination.',
          'Emotional context. Junk removal is typically routine. Estate cleanouts often involve family transitions.',
          'Pricing. Junk removal is priced by volume. Estate cleanouts are scoped from photos and property assessment.',
        ],
      },

      { type: 'h2', text: 'Which one do you need?' },
      { type: 'p', text: 'If you have a specific set of items to remove (furniture, appliances, a garage of boxes), junk removal is probably the fit. Send photos and an address. We give you a fast quote.' },
      { type: 'p', text: 'If you are dealing with a full home, an inherited property, a foreclosure, or a situation where you are not sure what is there or what needs to happen, that is an estate cleanout. We assess the full scope from photos. Give you an honest timeline. Coordinate the job with care.' },
      { type: 'p', text: 'Not sure which? Send photos and describe what you are working with. We will tell you exactly what we recommend. No pressure. No guessing.' },

      { type: 'h2', text: 'The Hearth Project and estate cleanouts.' },
      { type: 'p', text: 'Estate cleanouts often contain items that still have real value. Furniture. Clothing. Household goods. Collectibles.' },
      { type: 'p', text: 'Through the Hearth Project, we identify usable items during every estate cleanout. We redirect them to families and nonprofits in Central Indiana rather than sending everything to disposal.' },
      { type: 'p', text: 'For families going through an estate transition, knowing that a loved one\u2019s belongings are going somewhere meaningful, rather than to a landfill, brings real comfort. That is why the Hearth Project exists.' },

      { type: 'h2', text: 'Serving Central Indiana.' },
      { type: 'p', text: 'Ohana handles both junk removal and full estate cleanouts across Columbus, Indianapolis Southside, Bloomington, Greenwood, Franklin, Seymour, Edinburgh, Shelbyville, North Vernon, Greensburg, and surrounding Central Indiana communities.' },
      { type: 'p', text: 'Photo-based quotes. Honest timelines. 24 to 48 hour turnaround availability for standard junk removal jobs.' },
      { type: 'p', text: 'To get started, send photos of the property and your address to ohanas@ohanapropertyexperts.com or use the quote form on this site. We respond same day.' },
    ],
  },
];

export function getPostBySlug(slug) {
  return POSTS.find((p) => p.slug === slug);
}

export function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch (_) {
    return iso;
  }
}
